import type { ProtocolActor } from "@motokomaker/ic-client";

/**
 * Grant #user via initializeAccessControl (v1 auth bootstrap).
 * Safe to call repeatedly. Throws with actionable copy if still guest.
 */
export async function ensureProtocolUser(
	actor: ProtocolActor | null | undefined,
): Promise<void> {
	if (!actor) throw new Error("Not connected — open Internet Identity first");

	const bootstrap = async () => {
		await actor.initializeAccessControl();
	};

	try {
		await bootstrap();
	} catch (e) {
		const msg = e instanceof Error ? e.message : String(e);
		// Retry once — race with concurrent attaches
		try {
			await bootstrap();
		} catch {
			throw new Error(
				`Protocol access setup failed (${msg}). Reconnect II and retry.`,
			);
		}
	}

	if (typeof actor.getCallerUserRole === "function") {
		try {
			const role = await actor.getCallerUserRole();
			const key =
				role && typeof role === "object"
					? Object.keys(role as object)[0]
					: null;
			if (key === "guest") {
				// Second bootstrap attempt sometimes needed after first-ever admin assign
				try {
					await bootstrap();
					const role2 = await actor.getCallerUserRole();
					const key2 =
						role2 && typeof role2 === "object"
							? Object.keys(role2 as object)[0]
							: null;
					if (key2 === "guest") {
						throw new Error(
							"Still a guest on protocol — reconnect II or ask admin to grant user role.",
						);
					}
				} catch (e) {
					if (e instanceof Error && e.message.includes("guest")) throw e;
					throw new Error(
						"Still a guest on protocol — reconnect II or ask admin to grant user role.",
					);
				}
			}
		} catch (e) {
			if (e instanceof Error && e.message.includes("guest")) throw e;
			// getCallerUserRole missing/failed — still proceed; write will enforce
		}
	}
}
